import React from "react";
import { Link } from "react-router-dom";
import Wordmark from "../Wordmark.jsx";
import { APP_VERSION, APP_COMMIT } from "../../../buildInfo.generated.js";

const COLUMNS = [
  {
    heading: "Product",
    links: [
      { to: "/how-it-works", label: "How it works" },
      { to: "/rates", label: "Weight categories & rates" },
      { to: "/form-2290-tax-calculator", label: "Form 2290 tax calculator" },
      { to: "/form-2290-due-date-calculator", label: "Due date calculator" },
      { to: "/form-2290-reject-codes", label: "Reject codes" },
      { to: "/es", label: "Español" },
      { to: "/pricing", label: "Pricing" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  {
    // Anchor text is a ranking signal, and these are sitewide links — whatever
    // they say is what every page on the site says about the target. Bare labels
    // like "Amendments" or "Due dates 2026–2027" read fine in a footer column
    // but describe the destination to a crawler without ever naming the thing
    // the destination is about. Each label now carries its own keyword.
    //
    // Deliberately not all eight guides: a sitewide footer that links every
    // article dilutes each link and buries the ones that matter. VIN correction
    // is here because it is one of the differentiators SEO_COMPETITOR_ANALYSIS
    // flags as thinly covered everywhere else.
    heading: "Blog",
    links: [
      { to: "/blog", label: "All Form 2290 guides" },
      { to: "/blog/what-is-form-2290", label: "What is Form 2290?" },
      { to: "/blog/form-2290-due-date-2026-2027", label: "Form 2290 due dates 2026–2027" },
      { to: "/blog/stamped-schedule-1-proof-of-payment", label: "Stamped Schedule 1" },
      { to: "/blog/form-2290-vin-correction", label: "Form 2290 VIN correction" },
      { to: "/blog/form-2290-amendments", label: "Form 2290 amendments" },
      { to: "/blog/form-2290-credits-and-refunds", label: "Form 2290 credits & refunds" },
      { to: "/blog/bulk-form-2290-filing-for-fleets", label: "Bulk Form 2290 filing" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/about", label: "About EasyFile2290" },
      { to: "/contact", label: "Contact" },
      { to: "/login", label: "Sign in" },
      { to: "/login?tab=up", label: "Create account" },
    ],
  },
];

const Footer = () => (
  <div className="border-t border-[var(--line)] bg-[var(--panel)] mt-16">
    <div className="max-w-[1180px] mx-auto px-[22px] py-[38px]">
      <div className="flex flex-wrap justify-between gap-10">
        <div className="max-w-[320px]">
          <Link
            to="/"
            className="inline-flex items-center no-underline text-[var(--ink)]"
          >
            <Wordmark size="sm" />
          </Link>
          {/* Brand + domain in sitewide text (2026-10-22): every page now says
              what EasyFile2290 is, in words, not just in the logo image. */}
          <p className="text-[var(--muted)] text-[13px] mt-3">
            EasyFile2290 (easyfile2290.com) e-files Form 2290 Heavy Highway Vehicle Use Tax returns, figured with
            the IRS TY2026 rate tables (tax period July 1, 2026 – June 30, 2027).
          </p>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.heading}>
            <div className="font-disp uppercase tracking-[.12em] text-[11px] text-[var(--muted)] font-semibold mb-2.5">
              {col.heading}
            </div>
            <div className="flex flex-col gap-2">
              {col.links.map((l) => (
                <Link key={l.to + l.label} to={l.to} className="text-[13px] text-[var(--ink)] no-underline hover:text-[var(--accent)]">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] mt-8 pt-5 text-[12px] text-[var(--dim)]">
        <span>© 2026 Quantiva LLC · EasyFile2290 is a product of Quantiva LLC.</span>
        {/* Which build is live - see scripts/write-build-info.mjs. */}
        <span data-app-version={APP_VERSION} data-app-commit={APP_COMMIT}>
          Version {APP_VERSION} ({APP_COMMIT})
        </span>

        {/* Legal links live here for two reasons. The obvious one: this is where
            people look for them. The load-bearing one: react-snap discovers
            pages by crawling links from "/", so /privacy and /terms were listed
            in sitemap.xml but never prerendered — they were only linked from
            inside /login, which the crawl does not reach. A sitemap that points
            crawlers at an empty SPA shell is worse than not listing the page. */}
        <span className="flex items-center gap-4">
          <Link to="/privacy" className="text-[var(--dim)] no-underline hover:text-[var(--accent)]">
            Privacy Policy
          </Link>
          <Link to="/terms" className="text-[var(--dim)] no-underline hover:text-[var(--accent)]">
            Terms of Service
          </Link>
        </span>
      </div>
    </div>
  </div>
);

export default Footer;
